import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Moon, Wind, Volume2, VolumeX, Smartphone, Brain, Heart, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * REST ONBOARDING - Configuración de Descanso Consciente
 * 
 * Flujo de 4 preguntas para determinar el programa de descanso ideal
 */

const RestOnboarding = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Contenido de las pantallas
  const screens = [
    // Pantalla intro
    {
      type: 'intro',
      title: 'Tu descanso también se entrena',
      content: (
        <div className="text-center space-y-4">
          <p className="text-zinc-400 leading-relaxed">
            No todas las personas descansan igual.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            Hay quien recupera mejor en silencio.<br/>
            Hay quien necesita una guía sencilla para bajar el ritmo.<br/>
            Y hay quien necesita una pausa más estructurada para no seguir acelerado por dentro.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-6">
            Por eso, en <span className="text-primary font-semibold">GUARDIÁNSFIT</span>, el descanso no se deja al azar.
          </p>
          <p className="text-zinc-300 font-medium mt-4">
            Se adapta a cómo regula mejor cada persona.
          </p>
          <p className="text-zinc-500 text-sm mt-6 italic">
            Tu pausa también forma parte del entrenamiento.
          </p>
        </div>
      )
    },
    // Pregunta 1
    {
      type: 'question',
      question: '¿Cómo prefieres descansar?',
      key: 'prefers_guided',
      options: [
        { value: false, label: 'En silencio', icon: VolumeX, description: 'Sin guías ni estímulos' },
        { value: true, label: 'Con una guía simple', icon: Volume2, description: 'Indicaciones suaves' }
      ]
    },
    // Pregunta 2
    {
      type: 'question',
      question: '¿Te ayuda seguir una respiración marcada?',
      key: 'likes_breathing',
      options: [
        { value: true, label: 'Sí', icon: Wind, description: 'Me ayuda a regular' },
        { value: false, label: 'No', icon: VolumeX, description: 'Prefiero sin guía' },
        { value: null, label: 'No lo sé', icon: Brain, description: 'Nunca lo he probado' }
      ]
    },
    // Pregunta 3
    {
      type: 'question',
      question: 'Durante el descanso, ¿te cuesta quedarte quieto?',
      key: 'hard_to_stay_still',
      options: [
        { value: true, label: 'Sí', icon: Brain, description: 'Me cuesta parar' },
        { value: false, label: 'No', icon: Moon, description: 'Puedo estar tranquilo' },
        { value: null, label: 'A veces', icon: Heart, description: 'Depende del día' }
      ]
    },
    // Pregunta 4
    {
      type: 'question',
      question: '¿Sueles coger el móvil entre series o pausas?',
      key: 'uses_phone_between_sets',
      options: [
        { value: 'always', label: 'Sí, casi siempre', icon: Smartphone, description: 'Es automático' },
        { value: 'sometimes', label: 'A veces', icon: Smartphone, description: 'De vez en cuando' },
        { value: 'never', label: 'No', icon: Moon, description: 'Evito distracciones' }
      ]
    }
  ];

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = async () => {
    if (step < screens.length - 1) {
      setStep(step + 1);
    } else {
      // Enviar respuestas
      await submitOnboarding();
    }
  };

  const submitOnboarding = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/me/rest-onboarding`, answers, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(response.data);
      setStep(screens.length); // Ir a pantalla de resultado
    } catch (error) {
      toast.error('Error al guardar preferencias');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return true;
    const currentScreen = screens[step];
    if (currentScreen.type === 'question') {
      return answers[currentScreen.key] !== undefined;
    }
    return true;
  };

  // Pantalla de resultado
  const ResultScreen = () => {
    const programInfo = result?.program_info || {};
    const programIcons = {
      'respiracion_4_6': Wind,
      'silencio': VolumeX,
      'escaneo_corporal': Heart,
      'mentes_inquietas': Brain
    };
    const ProgramIcon = programIcons[result?.program] || Moon;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-6"
      >
        <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
          <ProgramIcon className="w-10 h-10 text-primary" />
        </div>
        
        <div>
          <p className="text-zinc-500 text-sm uppercase tracking-wider mb-2">Tu descanso recomendado</p>
          <h2 className="text-2xl font-bold text-white">{programInfo.title}</h2>
        </div>
        
        <p className="text-zinc-400 leading-relaxed max-w-sm mx-auto">
          {programInfo.description}
        </p>
        
        <div className="pt-6 space-y-3">
          <Button
            onClick={() => onComplete?.(result?.program)}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="rest-onboarding-accept"
          >
            <Check className="w-4 h-4 mr-2" />
            Empezar con este modo
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => onComplete?.(result?.program)}
            className="w-full text-zinc-500 hover:text-zinc-300"
          >
            Cambiar modo manualmente
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Progress indicator */}
        {step < screens.length && step > 0 && (
          <div className="flex gap-1 mb-8 justify-center">
            {screens.slice(1).map((_, idx) => (
              <div
                key={idx}
                className={`h-1 w-8 rounded-full transition-colors ${
                  idx < step ? 'bg-primary' : idx === step - 1 ? 'bg-primary/50' : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step < screens.length ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {screens[step].type === 'intro' ? (
                <>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <Moon className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-4">
                      {screens[step].title}
                    </h1>
                  </div>
                  {screens[step].content}
                </>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-white text-center">
                    {screens[step].question}
                  </h2>
                  
                  <div className="space-y-3">
                    {screens[step].options.map((option, idx) => {
                      const Icon = option.icon;
                      const isSelected = answers[screens[step].key] === option.value;
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => handleAnswer(screens[step].key, option.value)}
                          className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                            isSelected 
                              ? 'border-primary bg-primary/10' 
                              : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                          }`}
                          data-testid={`rest-option-${screens[step].key}-${idx}`}
                        >
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-primary/20' : 'bg-zinc-800'
                          }`}>
                            <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-zinc-500'}`} />
                          </div>
                          <div>
                            <p className={`font-medium ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                              {option.label}
                            </p>
                            <p className="text-sm text-zinc-500">{option.description}</p>
                          </div>
                          {isSelected && (
                            <div className="ml-auto">
                              <Check className="w-5 h-5 text-primary" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                {step > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 border-zinc-700"
                  >
                    Atrás
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!canProceed() || submitting}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="rest-onboarding-next"
                >
                  {submitting ? 'Guardando...' : step === screens.length - 1 ? 'Ver resultado' : 'Continuar'}
                </Button>
              </div>

              {step === 0 && onSkip && (
                <button
                  onClick={onSkip}
                  className="w-full text-center text-zinc-600 text-sm hover:text-zinc-400 transition-colors"
                >
                  Saltar por ahora
                </button>
              )}
            </motion.div>
          ) : (
            <ResultScreen />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RestOnboarding;
